<template>
  <v-container v-if="$can('browse', 'Yacht')">
    <div class="headline mb-6">
      Yachts
    </div>

    <v-tabs
      v-model="view"
      background-color="transparent"
      class="ma-0"
    >
      <v-tab
        id="active"
        class="text-none"
        href="#active"
      >
        Active
      </v-tab>
      <v-tab
        id="arrived"
        class="text-none"
        href="#arrived"
      >
        Arrived to Base
      </v-tab>
      <v-tab
        id="incoming"
        class="text-none"
        href="#incoming"
      >
        Incoming
      </v-tab>
    </v-tabs>
    <v-divider class="mb-6" />

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <v-text-field
            v-model="filtersDirty.id"
            solo
            type="number"
            label="ID"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
            dense
          />
          <v-text-field
            v-model="filtersDirty.name"
            solo
            label="Name"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
            dense
          />
          <v-text-field
            v-model="filtersDirty.hull_number"
            solo
            hide-details
            label="Hull Number"
            clearable
            class="mb-2"
            dense
          />
          <v-autocomplete
            v-model="filtersDirty.yacht_manufacturer_id"
            solo
            hide-details
            clearable
            label="Yacht Manufacturer"
            dense
            :items="manufacturers.data"
            :loading="manufacturers.loading"
            item-value="id"
            item-text="name"
            autocomplete="off"
            class="mb-2"
          />
          <yacht-model-select
            v-model="filtersDirty.yacht_model_id"
            solo
            hide-details
            label="Yacht Model"
            clearable
            :items="yachtModelsData"
            :loading="yachtModels.loading"
            item-value="id"
            item-text="full_label"
            class="mb-2"
            dense
          />
          <v-autocomplete
            v-if="$can('view', 'Owner')"
            v-model="filtersDirty.owner_id"
            solo
            hide-details
            dense
            label="Yacht owner"
            clearable
            :items="owners.data"
            :loading="owners.loading"
            item-value="id"
            item-text="name"
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.delivery_year"
            solo
            type="number"
            label="Delivery Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.phase_out_year"
            solo
            type="number"
            label="Phase Out Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-select
            v-model="filtersDirty.finance_model_type"
            class="mb-2"
            solo
            label="Investment Model"
            :items="financeModelTypes.data"
            :loading="financeModelTypes.loading"
            item-text="name"
            item-value="type"
            hide-details
            clearable
            dense
          />
        </div>
      </v-col>
      <v-col
        cols="13"
      >
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="yachts.meta.from"
          :to="yachts.meta.to"
          :total="yachts.meta.total"
          :last-page="yachts.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="tableHeaders"
          :items="yachts.data"
          :loading="yachts.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="yachts.meta.total"
          hide-default-footer
          must-sort
          :mobile-breakpoint="0"
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <yacht-row
              :yacht="item"
              :view="view"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <div
              v-if="isFiltered"
              class="text-center py-4"
            >
              <div>
                There are no results for your search.
              </div>
              <div
                class="mt-2"
              >
                <v-btn
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </div>
            </div>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <div
              v-else
              class="text-center py-4"
            >
              There are no yachts at the moment.
            </div>
            <!--</editor-fold>-->
          </template>
        </v-data-table>
        <!--</editor-fold>-->
        <pagination
          :from="yachts.meta.from"
          :to="yachts.meta.to"
          :total="yachts.meta.total"
          :last-page="yachts.meta.last_page"
          :page.sync="pagination.page"
          class="mt-1 align-center"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
import Yacht from '@/models/Yacht'
import YachtModel from '@/models/YachtModel'
import YachtRow from '@/components/service/yachts/YachtRow'
import YachtModelSelect from '@/components/helpers/YachtModelSelect'
import Owner from '@/models/Owner'
import FinanceModel from '@/models/FinanceModel'

export default {
    name: 'YachtsIndex',
    components: {
      YachtModelSelect,
      YachtRow
    },
    mixins: [paginationMixin, filterableMixin, serviceCurrentLocationMixin],
    data() {
      return {
        view: 'active',
        yachts: {
          data: [],
          loading: false,
          meta: {}
        },
        yachtModels: {
          data: [],
          loading: false
        },
        sorting: {
          sortBy: 'name',
          desc: false
        },
        pagination: {
          perPage: 50
        },
        filtersClean: Yacht.emptyFilters,
        filtersDirty: cloneDeep(Yacht.emptyFilters),
        owners: {
          data: [],
          loading: false
        },
        financeModelTypes: {
          data: [],
          loading: false
        }
      }
    },
    computed: {
      tableHeaders() {
        if (this.view === 'active') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: false },
            { text: 'Location', value: 'current_base', sortable: false },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Flag', value: 'flag_country_id', sortable: true },
            { text: 'Engine Hours', value: 'current_engine_hours', sortable: false },
            { text: 'Investment Model', value: 'finance_model_id', sortable: true }
          ]
        } else if (this.view === 'incoming') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: false },
            { text: 'Destination', value: 'destination_base', sortable: false },
            { text: 'Planned arrival', value: 'final_delivery_estimated_end_at', sortable: true },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Flag', value: 'flag_country_id', sortable: true },
            { text: 'Investment Model', value: 'finance_model_id', sortable: true },
            { text: 'Nausys ID', value: 'nausys_ids', sortable: false }
          ]
        } else if (this.view === 'arrived') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: false },
            { text: 'Destination', value: 'destination_base', sortable: false },
            { text: 'Planned charter start', sortable: false },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Flag', value: 'flag_country_id', sortable: true },
            { text: 'Investment Model', value: 'finance_model_id', sortable: true },
            { text: 'Nausys ID', value: 'nausys_ids', sortable: false }
          ]
        }

        return []
      },
      manufacturers() {
        return this.$store.getters['settings/yachtManufacturers']
      },
      yachtModelsData() {
        return this.yachtModels.data.filter(ym => {
          if (!this.filtersDirty.yacht_manufacturer_id) return true

          return this.filtersDirty.yacht_manufacturer_id === ym.yacht_manufacturer_id
        })
      }
    },
    watch: {
      view: function (value) {
        if (value === 'incoming') {
          this.sorting.sortBy = 'final_delivery_estimated_end_at'
          this.sorting.desc = false
        } else {
          this.sorting.sortBy = 'name'
          this.sorting.desc = false
        }

        this.fetchYachts()
      }
    },
    created() {
      this.fetchYachts()
      this.fetchYachtModels()
      this.fetchOwners()
      this.fetchFinanceModelTypes()
    },
    methods: {
      onCurrentLocationUpdate: function() {
        this.onFiltersUpdate()
      },
      onFiltersUpdate: function() {
        this.resetPagination()
      },
      onPaginationUpdate: function() {
        this.fetchYachts()
      },
      fetchYachts: async function () {
        this.yachts.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        if (this.view === 'active') {
          queryParams = { ...queryParams, ...this.currentLocationQueryParams }
          queryParams.status = [ Yacht.STATUS_IN_USE ]
        } else if (this.view === 'incoming') {
          if (this.currentBase) queryParams.destination_base_id = this.currentBase.id
          if (!this.currentBase && this.currentCountry) queryParams.country_id = this.currentCountry.id
          queryParams.status = [ Yacht.STATUS_PLANNED, Yacht.STATUS_DELIVERY ]
          queryParams.exclude_delivered = true
        } else if (this.view === 'arrived') {
          if (this.currentBase) queryParams.destination_base_id = this.currentBase.id
          if (!this.currentBase && this.currentCountry) queryParams.country_id = this.currentCountry.id
          queryParams.status = [ Yacht.STATUS_DELIVERY ]
          queryParams.is_delivered = true
        }

        try {
          let api = this.view === 'incoming' || this.view === 'arrived' ? Yacht.api.fetchLogisticsYachts : Yacht.api.fetchYachts

          let result = await api(queryParams)

          this.yachts.data = result.data
          this.yachts.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachts.loading = false
        }
      },
      fetchYachtModels: async function () {
        this.yachtModels.loading = true
        try {
          this.yachtModels.data = await YachtModel.api.fetch({
            status: YachtModel.STATUS_ACTIVE
          })
        } catch (error) {

        } finally {
          this.yachtModels.loading = false
        }
      },
      async fetchOwners() {
        if (!this.$can('view', 'Owner')) return

        this.owners.loading = true

        try {
          this.owners.data = await Owner.api.getOwners()
        } catch (error) {
          throw error
        } finally {
          this.owners.loading = false
        }
      },
      async fetchFinanceModelTypes() {
        this.financeModelTypes.loading = true

        this.financeModelTypes.data = await FinanceModel.api.getTypes()

        this.financeModelTypes.loading = false
      }
    }
  }
</script>
